<template>
	<div>
		<div class="ts-flex ts-justify-between">
			<div class="ts-mb-4">
				<h1 class="page-header ts-mb-1">{{ $t('invoiceAdjustment.pageTitle') }}</h1>
				<p class="ts-text-xs ts-text-gray-500">{{ $t('invoiceAdjustment.subTitle') }}</p>
			</div>
			<ts-breadcrumb :items="[
				{ text: $t('toHomePage'), href: '/' },
				{
					text: $t('invoiceAdjustment.pageTitle'),
					href: '/payment-adjustments'
				},
				{
					text: $t('create'),
					active: true
				}
			]" />
		</div>
		<ts-loading-banner :loading="loading">
			<div class="ts-space-y-3">
				<referenceInvoice :invoice="referenceInvoice" @invoice-selected="handleInvoiceSelected" />
				<!-- Adjustment Input & Payment Methods Section -->
				<div class="ts-grid ts-grid-cols-1 lg:ts-grid-cols-1 ts-gap-6">
					<section
						class="ts-bg-white ts-rounded-xl ts-border ts-border-slate-200 dark:ts-border-slate-700 ts-shadow-sm ts-overflow-hidden">
						<div class="ts-grid ts-grid-cols-1 lg:ts-grid-cols-12">
							<adjustmentInput v-model="model" :classes="classes" @on-change="onClassChange" :errors="errors" />
							<paymentMethods v-model="model" :errors="errors" />
						</div>
					</section>
				</div>

				<!-- Invoice Detail Table -->
				<invoiceDetailTable v-model="model" :feeItems="feeItems" ref="detail" :errors="errors" />
				<footer class=" ts-rounded-md ts-bg-white ts-flex 
					ts-items-center ts-justify-end ts-gap-4 ts-p-4 ts-border-t ts-border-slate-200 dark:ts-border-slate-800">
					<a-checkbox v-model="model.is_print">
						{{ $t('invoiceAdjustment.preViewPDF') }}
					</a-checkbox>
					<ts-button @click="onCancel">
						{{ $t('cancel') }}
					</ts-button>
					<ts-button color="primary" :disabled="!referenceInvoice" @click="onSubmit">
						{{ $t('invoiceAdjustment.submitAdjustment') }}
					</ts-button>
				</footer>
			</div>
		</ts-loading-banner>

		<a-modal width="50%" centered v-model="print_pdf" :title="$t('pdf')" :maskClosable="false" :footer="null"
			:zIndex="1020" @cancel="closePDFmodal">
			<pdf-preview :src="src" :waiting="loading" height="70vh" mspin="30vh" />
		</a-modal>

	</div>
</template>

<script>
import moment from 'moment';
import { referenceInvoice, adjustmentInput, paymentMethods, invoiceDetailTable, pdfPreview } from '../components';
import { mapActions } from 'vuex';
import { groupBy } from 'lodash';
import { Errors } from 'form-backend-validation';

export default {
	name: "invoiceAdjustment",
	components: {
		referenceInvoice,
		adjustmentInput,
		paymentMethods,
		invoiceDetailTable,
		pdfPreview
	},
	data() {
		return {
			loading: false,
			print_pdf: false,
			referenceInvoice: null, // Will be populated when user selects an invoice
			classes: [],
			feeItems: {},
			src: '',
			errors: new Errors(),
			model: {
				received_payment: {},
				descriptions: '',
				new_class_id: '',
				adjustment_id: undefined,
				adj_date: moment().format('DD-MM-YYYY'),
				inv_id: null,
				new_inv_amount: 0,
				adj_amount: 0,
				next_payment_date: undefined,
				inv_detail_list: [],
				student_id: undefined,
				is_print: false,
			},
			defaultCurrency: null // This should be loaded from API or store
		}
	},
	created() {
		this.loadResources()
	},
	methods: {
		...mapActions('invoiceAjustment', [
			'onGetCurrency',
			'onGetExchangeRate',
			'onGetPaymentMethod',
			'onGetAcademicClass',
			'onGetBank',
			'onGetFeeType',
			'onGetFeeItems',
			'onSave'
		]),
		async loadResources() {
			this.loading = true
			await Promise.all([
				this.onGetCurrency(),
				this.onGetExchangeRate(),
				this.onGetPaymentMethod(),
				this.onGetBank(),
				this.onGetFeeType(),
			])
			this.loading = false
		},
		async handleInvoiceSelected(invoice) {
			this.loading = true
			this.classes = []
			// Set the selected invoice
			this.referenceInvoice = invoice
			// Load invoice items into the table
			if (invoice.invoiceDetail && invoice.invoiceDetail.length > 0) {
				this.model.inv_detail_list = []

				invoice.invoiceDetail.forEach(item => {
					this.model.inv_detail_list.push({
						old_fee_setup_id: item.fee_setup_id,
						fee_type_id: item.fee_type_id,
						fee_setup_id: item.fee_setup_id,
						old_fee_setup_name: item.fee_name_en,
						fee_description: item.fee_description,
						quantity: item.quantity || 1,
						fee_start_date: item.fee_start_date,
						fee_end_date: item.fee_end_date,
						payment_term_id: item.payment_term_id,
						duration_in_month: item.duration_in_month,
						fee_amount: item.fee_amount,
						ac_term_id: item.ac_term_id,
						grade_id: item.grade_id,
						shift_id: item.shift_id,
						payment_term_name: item.payment_term_name || '',
						pro_type_id: item.pro_type_id
					})
					this.setFeeItems(item)
				})
			}

			this.model.student_id = invoice.student_id
			this.model.inv_id = invoice.inv_id

			this.model.received_payment = {}
			this.model.descriptions = ''
			this.model.new_class_id = ''

			const classResponse = await this.onGetAcademicClass({
				acp_id: invoice.acp_id
			})
			this.classes = classResponse.data.data

			const details = this.$refs.detail
			if (details) {
				details.calculate()
			}

			this.loading = false
		},
		setFeeItems(item) {
			this.feeItems = Object.assign({}, this.feeItems, {
				[item.fee_type_id]: [
					{
						fee_type_id: item.fee_type_id,
						fee_setup_id: item.fee_setup_id,
						fee_name: item.fee_name_en,
						fee_start_date: item.fee_start_date,
						fee_end_date: item.fee_end_date,
						payment_term_id: item.payment_term_id,
						duration_in_month: item.duration_in_month,
						fee_amount: item.total_amount,
						ac_term_id: item.ac_term_id,
						grade_id: item.grade_id,
						shift_id: item.shift_id,
						payment_term_name: item.payment_term_name,
						pro_type_id: item.pro_type_id
					}
				]
			})
		},
		async onClassChange(value) {
			const { data } = value

			this.loading = true
			const groupedFees = groupBy(this.model.inv_detail_list, 'fee_type_id')

			const apiCalls = Object.keys(groupedFees).map(async (fee_type_id) => {
				const response = await this.onGetFeeItems({
					student_id: this.model.student_id,
					ac_term_id: data.ac_term_id,
					grade_id: data.grade_id,
					fee_type_id: Number(fee_type_id),
					room_type_id: data.room_type_id,
					inv_id: this.model.inv_id
				})

				const items = response.data.data[0] || []
				this.$set(this.feeItems, fee_type_id, [...items])

				return response;
			})

			await Promise.all(apiCalls)

			const hasDifferenceGrade = this.model.inv_detail_list
				.filter((el) => el.grade_id)
				.find((el) => el.grade_id !== data.grade_id);

			if (hasDifferenceGrade !== undefined) {
				this.model.inv_detail_list.forEach((el) => {
					el.fee_setup_id = undefined
					el.grade_id = data.grade_id
					el.ac_term_id = data.ac_term_id
				})
			}

			this.loading = false
		},
		onSubmit() {
			this.loading = true
			this.errors = new Errors()
			this.onSave(this.model).then(response => {
				this.$toasted.success(response.data.message)
				if (this.model.is_print) {
					this.print_pdf = true
					this.src = response.data.url
					this.clearInput()
				} else {
					this.$router.push({ name: 'payment-adjustment' })
				}
			})
				.catch(error => {
					this.$toasted.error(error.response.data.message)
					this.errors = new Errors(error.response.data.errors)
				})
				.finally(() => {
					this.loading = false
				})

		},
		clearInput() {
			this.model.received_payment = {}
			this.model.descriptions = ''
			this.model.new_class_id = ''
			this.model.adjustment_id = undefined
			this.model.adj_date = moment().format('DD-MM-YYYY')
			this.model.inv_id = null
			this.model.new_inv_amount = 0
			this.model.adj_amount = 0
			this.model.next_payment_date = undefined
			this.model.inv_detail_list = []
			this.model.student_id = undefined
			this.model.is_print = false

			this.classes = []
			this.referenceInvoice = null
		},
		closePDFmodal() {
			this.print_pdf = false
		},
		onCancel() {
			this.$router.go(-1);
		}
	}
}
</script>
